/**
 * DomDiagnosticCard — Admin tool to spot-check whether the Verified Demand
 * embed.js is installed correctly on a dealer's live website. The backend
 * fetches the URL server-side (SSRF-protected via safe_fetch) and returns a
 * closed, minimal result — no HTTP metadata from the fetched URL (status
 * code, content-type, CSP, headers) is ever returned, only whether the
 * dealer's own embed pattern was found on the page.
 *
 * Intended placement: inside DealerBrandingTab, below the Live Preview.
 * Permission: MASTER_ADMIN globally, or DEALER_ADMIN scoped to their own
 * business_id (enforced server-side at /api/diagnostics/dom-diagnostic).
 */
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Stethoscope, CheckCircle2, XCircle, AlertTriangle, HelpCircle, Loader2 } from "lucide-react";

const STATUS_BADGE = {
  detected: { icon: <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />, cls: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  not_detected: { icon: <XCircle size={16} className="text-red-600 shrink-0" />, cls: "border-red-200 bg-red-50 text-red-800" },
  unreachable: { icon: <AlertTriangle size={16} className="text-amber-600 shrink-0" />, cls: "border-amber-200 bg-amber-50 text-amber-800" },
  invalid_url: { icon: <AlertTriangle size={16} className="text-amber-600 shrink-0" />, cls: "border-amber-200 bg-amber-50 text-amber-800" },
  parse_error: { icon: <AlertTriangle size={16} className="text-amber-600 shrink-0" />, cls: "border-amber-200 bg-amber-50 text-amber-800" },
};

const BoolRow = ({ label, value, trueIsGood = true }) => {
  let icon = <HelpCircle size={14} className="text-gray-400 shrink-0" />;
  let text = "—";
  if (value !== null && value !== undefined) {
    const good = trueIsGood ? value : !value;
    icon = good ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : <XCircle size={14} className="text-red-600 shrink-0" />;
    text = value ? "Yes" : "No";
  }
  return (
    <div className="flex items-center justify-between text-sm py-1.5" data-testid={`dom-diag-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="text-gray-600">{label}</span>
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">{icon} {text}</span>
    </div>
  );
};

export const DomDiagnosticCard = ({ businessId }) => {
  const { apiFetch } = useAuth();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runDiagnostic = async () => {
    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await apiFetch("/diagnostics/dom-diagnostic", {
        method: "POST",
        data: { url: url.trim(), business_id: businessId || undefined },
      });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !loading) runDiagnostic();
  };

  const badge = result ? STATUS_BADGE[result.status] || STATUS_BADGE.unreachable : null;
  const keyLabel = { match: "Match", mismatch: "Mismatch", not_found: "Not found" };

  return (
    <Card data-testid="dom-diagnostic-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Stethoscope size={18} className="text-blue-600" />
          DOM Diagnostic
        </CardTitle>
        <CardDescription>
          Enter the dealer&apos;s live website URL to verify the Verified Demand embed
          is installed and configured correctly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="dom-diag-url">Website URL</Label>
          <div className="flex gap-2">
            <Input
              id="dom-diag-url"
              data-testid="dom-diag-url-input"
              type="url"
              placeholder="https://www.fordstoreofmorgantown.com/inventory"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
              className="flex-1"
            />
            <Button
              onClick={runDiagnostic}
              disabled={loading || !url.trim()}
              data-testid="dom-diag-run-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-1.5" /> Running…
                </>
              ) : (
                "Run Diagnostic"
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Tip: paste an inventory or VDP page where pricing is shown. The
            embed needs price elements to attach to.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="dom-diag-error">
            {error}
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="dom-diag-result">
            <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${badge.cls}`} data-testid="dom-diag-status-badge">
              {badge.icon} {result.message}
            </div>

            {(result.status === "detected" || result.status === "not_detected") && (
              <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 px-3">
                <BoolRow label="Embed script detected" value={result.install_detected} />
                {result.public_key_status && (
                  <div className="flex items-center justify-between text-sm py-1.5" data-testid="dom-diag-row-public-key">
                    <span className="text-gray-600">Public key</span>
                    <span className="font-medium text-gray-900">{keyLabel[result.public_key_status] || result.public_key_status}</span>
                  </div>
                )}
                <BoolRow label="Duplicate instances" value={result.duplicate_instances} trueIsGood={false} />
                <BoolRow label="Price elements present" value={result.price_element_present} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DomDiagnosticCard;
